import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Calendar, Users, Clock, Trophy, Tags, UserCheck, Package, Mail, Shield, Image, Settings, Award } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';
import BookingManagement from '../admin/BookingManagement';
import EventManagement from '../admin/EventManagement';
import CategoryManagement from '../admin/CategoryManagement';
import EventRegistrationManagement from '../admin/EventRegistrationManagement';
import TournamentManagement from '../admin/TournamentManagement';
import { FeatureId, PermissionMap } from '../../types/permissions';

export default function WorkerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState<PermissionMap>({});
  const [availableTabs, setAvailableTabs] = useState<Array<{id: string, name: string, icon: any, permission: FeatureId}>>([]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    const initializeData = async () => {
      try {
        await fetchPermissions();
      } catch (error) {
        console.error('Error initializing data:', error);
        if (error instanceof Error) {
          toast.error(error.message);
        } else {
          toast.error('Failed to initialize data');
        }
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, [user, navigate]);

  const fetchPermissions = async () => {
    try {
      const { data, error } = await supabase
        .from('user_permissions')
        .select('*')
        .eq('user_id', user?.id);

      if (error) throw error;

      const permMap: PermissionMap = {};
      (data || []).forEach((perm) => {
        permMap[perm.feature_id] = perm.can_access;
      });
      setPermissions(permMap);

      // Define all possible tabs with their icons and permissions
      const allTabs = [
        { id: 'bookings', name: 'Bookings', icon: Calendar, permission: 'order_management' as FeatureId },
        { id: 'users', name: 'Users', icon: Users, permission: 'user_management' as FeatureId },
        { id: 'events', name: 'Events', icon: Trophy, permission: 'event_management' as FeatureId },
        { id: 'categories', name: 'Categories', icon: Tags, permission: 'category_management' as FeatureId },
        { id: 'registrations', name: 'Event Registrations', icon: UserCheck, permission: 'event_registrations' as FeatureId },
        { id: 'tournaments', name: 'Tournaments', icon: Award, permission: 'event_management' as FeatureId },
        { id: 'services', name: 'Services', icon: Package, permission: 'track_management' as FeatureId },
        { id: 'calendar', name: 'Calendar', icon: Clock, permission: 'calendar_view' as FeatureId },
        { id: 'gallery', name: 'Gallery', icon: Image, permission: 'track_management' as FeatureId },
        { id: 'emails', name: 'Email Templates', icon: Mail, permission: 'email_templates' as FeatureId },
        { id: 'roles', name: 'Role Management', icon: Shield, permission: 'worker_management' as FeatureId },
        { id: 'settings', name: 'Settings', icon: Settings, permission: 'system_settings' as FeatureId }
      ];
      
      // Filter tabs based on user permissions
      const userTabs = allTabs.filter(tab => permMap[tab.permission]);
      setAvailableTabs(userTabs);
      
      // Set the first available tab as active if no tab is currently active
      if (!activeTab && userTabs.length > 0) {
        setActiveTab(userTabs[0].id);
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
      toast.error('Failed to load permissions');
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  if (availableTabs.length === 0) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-center px-4">
          You do not have access to any features in this dashboard. Please contact your administrator.
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Tab Navigation */}
      <div className="bg-zinc-900 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8 overflow-x-auto">
            {availableTabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center px-1 py-4 border-b-2 text-sm font-medium whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-500'
                      : 'border-transparent text-gray-400 hover:text-white hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-5 w-5 mr-2" />
                  {tab.name}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="py-10">
        {activeTab === 'bookings' && <BookingManagement />}
        {activeTab === 'events' && <EventManagement />}
        {activeTab === 'categories' && permissions['category_management'] && <CategoryManagement />}
        {activeTab === 'registrations' && <EventRegistrationManagement />}
        {activeTab === 'tournaments' && permissions['event_management'] && <TournamentManagement />}
        {activeTab === 'gallery' && permissions['track_management'] && <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"><h1 className="text-3xl font-bold text-white mb-8">Gallery Management</h1></div>}
        {activeTab === 'services' && permissions['track_management'] && <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"><h1 className="text-3xl font-bold text-white mb-8">Service Management</h1></div>}
        {activeTab === 'users' && permissions['user_management'] && <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"><h1 className="text-3xl font-bold text-white mb-8">User Management</h1></div>}
        {activeTab === 'calendar' && permissions['calendar_view'] && <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"><h1 className="text-3xl font-bold text-white mb-8">Calendar View</h1></div>}
        {activeTab === 'emails' && permissions['email_templates'] && <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"><h1 className="text-3xl font-bold text-white mb-8">Email Templates</h1></div>}
        {activeTab === 'roles' && permissions['worker_management'] && <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"><h1 className="text-3xl font-bold text-white mb-8">Role Management</h1></div>}
        {activeTab === 'settings' && permissions['system_settings'] && <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"><h1 className="text-3xl font-bold text-white mb-8">System Settings</h1></div>}
      </div>
    </div>
  );
}
